import {
  HiArrowPath,
  HiCheckCircle,
  HiExclamationTriangle,
} from "react-icons/hi2";

/**
 * "Pano has mirrored this account's signals into HubSpot" — PAN-677.
 *
 * Account-level on purpose, not per signal row. Pano writes ONE set of company
 * properties describing the whole account, so a per-row icon would imply a per-signal
 * sync that does not exist and cannot be acted on.
 *
 * Renders nothing when `sync` is null, which is what the backend sends for a team
 * without the feature. That is also why the backend sends null rather than a
 * zero-valued object: "not enabled" and "enabled, nothing synced yet" are different
 * states and only the second deserves an icon.
 */

const RELATIVE_THRESHOLDS = [
  [60, "just now"],
  [3600, (s) => `${Math.floor(s / 60)}m ago`],
  [86400, (s) => `${Math.floor(s / 3600)}h ago`],
];

export const formatSyncedAt = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const seconds = Math.max(0, (Date.now() - then.getTime()) / 1000);
  for (const [limit, label] of RELATIVE_THRESHOLDS) {
    if (seconds < limit)
      return typeof label === "function" ? label(seconds) : label;
  }
  return `${Math.floor(seconds / 86400)}d ago`;
};

const VARIANTS = {
  synced: {
    Icon: HiCheckCircle,
    className: "text-emerald-500",
    label: (when) =>
      when ? `Signals synced to HubSpot ${when}` : "Signals synced to HubSpot",
  },
  skipped: {
    Icon: HiCheckCircle,
    className: "text-emerald-500",
    label: (when) =>
      when ? `Signals synced to HubSpot ${when}` : "Signals synced to HubSpot",
  },
  pending: {
    Icon: HiArrowPath,
    className: "text-gray-400",
    label: () => "Syncing signals to HubSpot…",
  },
  failed: {
    Icon: HiExclamationTriangle,
    className: "text-amber-500",
    label: () => "Couldn't sync signals to HubSpot — Pano will retry",
  },
};

export const HubspotSyncIndicator = ({ sync }) => {
  if (!sync) return null;

  // A status we do not know about is not rendered as "fine" — it is not rendered at
  // all. Inventing a green tick for an unrecognised state is how a stuck writeback
  // would look healthy.
  const variant = VARIANTS[sync.status];
  if (!variant) return null;

  const { Icon, className, label } = variant;
  // `skipped` means "nothing changed, so nothing was sent" — for a CSM that IS synced,
  // and the last successful write is the honest timestamp to show.
  const title = label(formatSyncedAt(sync.last_synced_at));

  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-gray-400"
      title={title}
      data-testid="hubspot-sync-indicator"
      data-status={sync.status}
    >
      <Icon
        className={`size-3.5 ${className} ${
          sync.status === "pending" ? "animate-spin" : ""
        }`}
        aria-hidden="true"
      />
      <span className="sr-only">{title}</span>
      <span aria-hidden="true">HubSpot</span>
    </span>
  );
};
